/**
 * Which note is open, and the editor's working copy of it.
 *
 * Extracted from `ApiNotesEditor` inside `NotesViewer.tsx`. Unlike the pure
 * helpers in `lib/notes/preview.ts`, this owns two `useEffect`s, so the risk
 * here is effect *ordering* — which `tsc` cannot see and a shallow render test
 * would not catch. It is covered by `renderHook` tests that exercise the real
 * effect timing, and the component's flattened hook order is unchanged (the two
 * effects still run in the same relative position they did inline).
 *
 * The two effects, and why each is shaped the way it is:
 *
 * 1. **Auto-select the first note** once the list loads. Guarded on
 *    `!selectedNoteId` so it only ever fires for the initial selection — without
 *    that guard, deleting the open note would snap the user back to note #1
 *    mid-edit.
 *
 * 2. **Re-sync the buffer when the open note changes identity.** Keyed on
 *    `selectedNote?.note_id` alone, NOT on the content — deliberate, because
 *    depending on content would re-run on every keystroke and fight the user's
 *    typing (the buffer *is* the thing being edited).
 *
 * The buffer is intentionally allowed to diverge from the server between saves,
 * which is why this is state and not a `useMemo`.
 *
 * ---
 * **Finding, carried over verbatim in behaviour but not in description.**
 *
 * Effect (2) arrived with the comment "sync editor when selected note changes
 * from external updates". It cannot do that: an external content change leaves
 * `note_id` untouched, so the effect never re-runs. And on the path where
 * `note_id` *does* change, `selectNote` has already written the new note's
 * content into the buffer, so the inequality is false and the body no-ops.
 *
 * Probed every route I could construct — including switching notes mid-save —
 * and effect (2) was a no-op in all of them, which also makes its `isSaving`
 * guard unreachable.
 *
 * It is preserved as-is rather than deleted. Removing an effect that is
 * *probably* dead from a component with no render tests is a product decision,
 * not a refactor, and the tests below pin the current behaviour either way. If
 * live re-sync is actually wanted, the fix is to key on content and reconcile
 * against a dirty flag — not to widen this dependency array.
 */
import { useCallback, useEffect, useState } from 'react';

/** The subset of a note this hook needs. Structural, so it fits `NoteItem`. */
export interface EditableNote {
  note_id: string;
  title: string;
  content: string;
}

export interface UseNoteEditorBufferArgs<T extends EditableNote> {
  /** The notes list, once loaded. */
  notes: T[];
  /** True while a save is in flight — suppresses re-sync from the server. */
  isSaving: boolean;
}

export function useNoteEditorBuffer<T extends EditableNote>({
  notes,
  isSaving,
}: UseNoteEditorBufferArgs<T>) {
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorTitle, setEditorTitle] = useState('');

  const selectedNote = notes.find(n => n.note_id === selectedNoteId) ?? null;

  const selectNote = useCallback((note: T) => {
    setSelectedNoteId(note.note_id);
    setEditorContent(note.content);
    setEditorTitle(note.title);
  }, []);

  /** Deselect and empty the buffer — used after deleting the last note.
   *
   *  A method rather than exposing `setSelectedNoteId`: clearing the id without
   *  also clearing the buffer would leave the previous note's text on screen
   *  with nothing selected, and re-arm effect (1) to auto-select note #1 while
   *  that stale text is still in the editor. Keeping the three writes together
   *  is the invariant. */
  const clearSelection = useCallback(() => {
    setSelectedNoteId(null);
    setEditorContent('');
    setEditorTitle('');
  }, []);

  // (1) Initial selection only — see the note on the guard above.
  useEffect(() => {
    if (notes.length > 0 && !selectedNoteId) {
      selectNote(notes[0]);
    }
  }, [notes.length, selectedNoteId, selectNote]);

  // (2) Adopt the server's copy when the OPEN NOTE CHANGES, never on content.
  useEffect(() => {
    if (!selectedNote) return;
    if (editorContent !== selectedNote.content && !isSaving) {
      setEditorContent(selectedNote.content);
    }
    if (editorTitle !== selectedNote.title && !isSaving) {
      setEditorTitle(selectedNote.title);
    }
  }, [selectedNote?.note_id]);

  return {
    selectedNoteId,
    selectedNote,
    selectNote,
    clearSelection,
    editorContent,
    setEditorContent,
    editorTitle,
    setEditorTitle,
  };
}
